import type { AbpCurrentUserDto } from '@/features/auth/api/auth.dto';
import type { AuthUser } from '@/features/auth/types/auth.types';

function dedupeRoles(roles: string[]): string[] {
  return [...new Set(roles.map((r) => r.trim()).filter(Boolean))];
}

function normalizeRoles(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const x of raw) {
    if (typeof x === 'string' && x.trim()) {
      out.push(x.trim());
      continue;
    }
    if (x && typeof x === 'object') {
      const o = x as Record<string, unknown>;
      const n = o.name ?? o.Name ?? o.roleName ?? o.RoleName;
      if (typeof n === 'string' && n.trim()) out.push(n.trim());
    }
  }
  return dedupeRoles(out);
}

function readCurrentUser(config: unknown): AbpCurrentUserDto | null {
  if (!config || typeof config !== 'object') return null;
  const root = config as Record<string, unknown>;
  const wrapped = root.result ?? root.Result ?? root.data ?? root.Data;
  const payload =
    wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)
      ? (wrapped as Record<string, unknown>)
      : root;
  const cu = payload.currentUser ?? payload.CurrentUser;
  if (!cu || typeof cu !== 'object') return null;
  return cu as AbpCurrentUserDto;
}

/**
 * Builds {@link AuthUser} from ABP `GET /api/abp/application-configuration` (no Identity / my-profile calls).
 */
export function authUserFromAbpApplicationConfiguration(config: unknown): AuthUser | null {
  const cu = readCurrentUser(config);
  if (!cu) return null;
  if (cu.isAuthenticated === false) return null;

  const id = typeof cu.id === 'string' ? cu.id.trim() : cu.id != null ? String(cu.id).trim() : '';
  const userName =
    typeof cu.userName === 'string'
      ? cu.userName.trim()
      : cu.userName != null
        ? String(cu.userName).trim()
        : '';
  const email =
    typeof cu.email === 'string' ? cu.email.trim() : cu.email != null ? String(cu.email).trim() : '';

  if (!id && !userName && !email) return null;

  const sur = cu.surName ?? cu.surname ?? (cu as { Surname?: string }).Surname;
  const namePart = [cu.name, sur].filter((x) => typeof x === 'string' && x.trim()).join(' ').trim();

  const roles = normalizeRoles(cu.roles ?? (cu as { Roles?: unknown }).Roles);

  return {
    id: id || undefined,
    userName: userName || undefined,
    email: email || undefined,
    name: namePart || undefined,
    roles,
  };
}
